<script setup>
import PieChart from '@ohrm/oxd/core/components/Chart/PieChart.vue';
import {ref, defineProps, onBeforeMount, watch, inject} from 'vue';
import FileInput from '../../../orangehrmPimPlugin/components/FileInput.vue';
import {APIService} from '@/core/util/services/api.service';
import debounce from '@ohrm/oxd/utils/debounce';
import {QualificationStatusList} from '@/orangehrmTrainigPlugin/constants/QualificationStatus';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import {formatDate} from '@/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';

const $toast = inject('$toast');
const {jsDateFormat} = useDateFormat();

const props = defineProps({
  id: Number,
});

const http = new APIService(window.appGlobal.baseUrl, '');

const isLoading = ref(false);
const isLoadingParticipanDetails = ref(false);
const isLoadingPieChart = ref(false);
const selectedEmployee = ref(null);

const employees = ref([]);

const qualificationForm = ref({
  status: null,
  remarks: null,
  managerSignature: null,
});

const pieChartData = ref([]);
const hasAssignees = ref(false);

const getPieChartData = () => {
  isLoadingPieChart.value = true;
  http
    .request({
      method: 'GET',
      url: `/api/onboarding/${props.id}/pie-chart-data`,
    })
    .then(response => {
      pieChartData.value = [
        {
          value: response.data.done,
          color: '#3bba14',
          label: 'Done',
        },
        {
          value: response.data.notDone,
          color: '#ed0c22',
          label: 'Not Done',
        },
      ];
      hasAssignees.value = response.data.hasAssignees;
    })
    .finally(() => {
      isLoadingPieChart.value = false;
    });
};

const onSave = () => {

  isLoadingParticipanDetails.value = true;
  const formData = new FormData();
  formData.append('status', qualificationForm.value.status?.id ?? null);
  formData.append('remarks', qualificationForm.value.remarks);

  if (
    qualificationForm.value.employeeSignature?.value instanceof File ||
    qualificationForm.value.employeeSignature == null
  ) {
    formData.append(
      'employee_signature',
      qualificationForm.value.employeeSignature?.value ?? null,
    );
  }

  if (
    qualificationForm.value.managerSignature?.value instanceof File ||
    qualificationForm.value.managerSignature == null
  ) {
    formData.append(
      'manager_signature',
      qualificationForm.value.managerSignature?.value ?? null,
    );
  }

  http
    .request({
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      url: `/api/onboarding/${props.id}/assignie/${selectedEmployee.value?.id}/qualification`,
      data: formData,
    })
    .then(response => {
      $toast.saveSuccess();
    })
    .catch(error => {
      $toast.unexpectedError();
      console.error(error);
    })
    .finally(() => {
      isLoadingParticipanDetails.value = false;
    });
};

const getQualificationData = () => {
  isLoadingParticipanDetails.value = true;
  http
    .request({
      method: 'GET',
      url: `/api/onboarding/${props.id}/assignie/${selectedEmployee.value?.id}/qualification`,
    })
    .then(response => {
      qualificationForm.value = response.data;
    })
    .finally(() => {
      isLoadingParticipanDetails.value = false;
    });
};

watch(
  selectedEmployee,
  debounce(() => {
    getQualificationData();
  }, 500),
  {deep: true},
);

const getAssignEmployees = () => {
  isLoadingParticipanDetails.value = true;
  http
    .request({
      method: 'GET',
      url: '/api/onboarding/assignedEmployees',
      params: {onboardingId: props.id},
    })
    .then(response => {
      employees.value = response.data;
    })
    .finally(() => {
      isLoadingParticipanDetails.value = false;
    });
};

const addCoWorkerSignature = event => {
  qualificationForm.value.employeeSignature = {
    label: event.target.files[0].name ?? '',
    value: event.target.files[0],
  };
};

const removeCoWorkerSignature = () => {
  qualificationForm.value.employeeSignature = null;
};

const addManagerSignature = event => {
  qualificationForm.value.managerSignature = {
    label: event.target.files[0].name ?? '',
    value: event.target.files[0],
  };
};

const removeManagerSignature = () => {
  qualificationForm.value.managerSignature = null;
};

onBeforeMount(() => {
  getAssignEmployees();
  getPieChartData();
});
</script>
<template>
  <oxd-form :loading="isLoading">
    <oxd-form-row>
      <oxd-table-filter filter-title="Overview" class="!px-0">
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <PieChart
              v-if="hasAssignees"
              :data="pieChartData"
              :aspect-ratio="false"
              :custom-legend="true"
              :custom-tooltip="true"
              wrapper-classes="chart"
            />
            <oxd-text v-else tag="p">
              {{ $t('onboarding.no_assignees') }}
            </oxd-text>
          </oxd-grid-item>
        </oxd-grid>
      </oxd-table-filter>
    </oxd-form-row>
  </oxd-form>
  <oxd-table-filter filter-title="Participant Details" class="!px-0 !pb-0">
    <oxd-form
      :loading="isLoadingParticipanDetails || isLoadingPieChart"
      @submitValid="onSave"
    >
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="selectedEmployee"
            label="Employee Name"
            type="select"
            :options="employees"
          />
        </oxd-grid-item>
      </oxd-grid>
      <oxd-form-row>
        <template v-if="selectedEmployee?.id">
          <oxd-form-row>
            <oxd-table-filter filter-title="Qualifications" class="!pt-0">
              <oxd-grid :cols="12" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <table class="w-full custom-table">
                    <tr>
                      <th>
                        <oxd-text tag="p">Type Of Activity</oxd-text>
                      </th>
                      <th>
                        <oxd-text tag="p">Activity</oxd-text>
                      </th>
                      <th>
                        <oxd-text tag="p">Manager</oxd-text>
                      </th>
                      <th>
                        <oxd-text tag="p">Due Date</oxd-text>
                      </th>
                      <th>
                        <oxd-text tag="p">Complete Date</oxd-text>
                      </th>
                    </tr>
                    <tr
                      v-for="(activity, index) in qualificationForm.activities"
                      :key="index"
                    >
                      <td>
                        <oxd-text tag="p">{{
                          activity.typeOfActivity?.label
                        }}</oxd-text>
                      </td>
                      <td>
                        <oxd-text tag="p">{{
                          activity.activity?.label
                        }}</oxd-text>
                      </td>
                      <td>
                        <oxd-text tag="p">{{
                          activity.responsible?.label ?? '-'
                        }}</oxd-text>
                      </td>
                      <td>
                        <oxd-text tag="p">{{
                          activity.dueDate
                            ? formatDate(
                                new Date(activity.dueDate),
                                jsDateFormat,
                              )
                            : '-'
                        }}</oxd-text>
                      </td>
                      <td>
                        <oxd-text tag="p" class="--error">{{
                          activity.completeDate
                            ? formatDate(
                                new Date(activity.completeDate),
                                jsDateFormat,
                              )
                            : '-'
                        }}</oxd-text>
                      </td>
                    </tr>
                  </table>
                </oxd-grid-item>
              </oxd-grid>
            </oxd-table-filter>
          </oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="qualificationForm.status"
                  type="select"
                  label="Qualification Status"
                  :options="QualificationStatusList"
                />
              </oxd-grid-item>
              <oxd-grid-item class="--span-column-2">
                <oxd-input-field
                  v-model="qualificationForm.remarks"
                  type="textarea"
                  label="Remarks"
                  placeholder="Remarks"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="2">
              <oxd-grid-item>
                <FileInput
                  :disabled="qualificationForm.managerSignature != null"
                  label="Manager signature"
                  @change="addManagerSignature"
                ></FileInput>
                <MultiChips
                  v-if="qualificationForm.managerSignature != null"
                  :selected="[qualificationForm.managerSignature]"
                  @chip-removed="removeManagerSignature"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
        </template>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <submit-button />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
</template>
